import { useCallback } from "react";
import { Linking, Platform, ScrollView, useWindowDimensions } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import {
  initialWindowMetrics,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { TRPCClientError } from "@trpc/client";
import {
  MERCH_ORDER_COPY,
  createMerchOrderModel,
  type MerchOrderModel,
} from "@libs/shared-ui";
import type { Access, AccessTypeMerchType } from "@libs/wire";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LPressable from "@components/LPressable";
import BackgroundCircles from "@components/Accesses/AccessDetails/components/common/Background";
import Handle from "@components/Accesses/AccessDetails/components/common/Handle";
import Close from "@components/Accesses/AccessDetails/components/common/Close";
import Contact from "@components/Accesses/AccessDetails/components/event/Contact";
import { THREE_BUTTON_LAYOUT_BUTTON_WIDTH } from "@components/Accesses/AccessDetails/config";
import { useAccess } from "@hooks/trpc/useAccesses";
import { useMerchOrder } from "@hooks/trpc/useMerchOrder";
import { useMe } from "@hooks/trpc/useMe";
import { getViewPort } from "@utils/web/config";
import { theme } from "@theme";

/** A label over its value, as My Tickets lays out venue and address. */
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <LBox gap="s1">
    <LText variant="Body/B2/Medium" color="whiteTranslucent60">
      {label}
    </LText>
    {children}
  </LBox>
);

/** How a posted order comes: who posts it, to the address on its own read. */
const Posted = ({ order }: { order: MerchOrderModel }) => (
  <LBox gap="s1">
    <LText variant="Body/B1/SemiBold">{order.postedBy}</LText>
    {order.addressLines ? (
      <>
        <LText variant="Body/B2/Medium" color="whiteTranslucent60">
          {MERCH_ORDER_COPY.delivery}
        </LText>
        <LText variant="Body/B1/Medium">
          {order.addressLines.join("\n")}
        </LText>
      </>
    ) : null}
  </LBox>
);

/** How a digital download comes: waiting for the store's email, or
 *  emailed on, with its Download button once the order's own read has its
 *  link. */
const Download = ({
  download,
}: {
  download: NonNullable<MerchOrderModel["download"]>;
}) => (
  <LBox gap="s2">
    <LBox gap="s1">
      <LText variant="Body/B1/SemiBold">{download.title}</LText>
      <LText variant="Body/B2/Medium" color="whiteTranslucent60">
        {download.detail}
      </LText>
    </LBox>
    {download.url ? (
      <LPressable
        accessibilityRole="button"
        onPress={() => void Linking.openURL(download.url!)}
        sentryEventName="MerchOrder.Download"
        style={{
          alignItems: "center",
          alignSelf: "flex-start",
          backgroundColor: theme.colors.white,
          borderRadius: 999,
          height: 40,
          justifyContent: "center",
          paddingHorizontal: theme.spacing.m1,
        }}
      >
        <LText variant="Body/B2/Medium" color="black">
          {MERCH_ORDER_COPY.download}
        </LText>
      </LPressable>
    ) : null}
  </LBox>
);

/** One order: when, what, who posts it where (or a download's state),
 *  when it shipped, the store's reference and the total. No code to show,
 *  nothing to collect: the store posts or emails it. */
const OrderCard = ({ order }: { order: MerchOrderModel }) => (
  <LBox
    bg="whiteTranslucent10"
    borderRadius="m"
    p="m1"
    gap="m1"
    testID={`merch-order-${order.key}`}
  >
    <Row label={MERCH_ORDER_COPY.ordered}>
      <LText variant="Body/B1/Medium">{order.ordered}</LText>
    </Row>
    <Row label={MERCH_ORDER_COPY.items}>
      {order.items.map((item) => (
        <LText key={item.key} variant="Body/B1/Medium">
          {item.text}
        </LText>
      ))}
    </Row>
    {order.download ? (
      <Download download={order.download} />
    ) : (
      <Posted order={order} />
    )}
    {order.shipped ? (
      <Row label={MERCH_ORDER_COPY.shipped}>
        <LText variant="Body/B1/Medium">{order.shipped}</LText>
      </Row>
    ) : null}
    {order.reference ? (
      <Row label={order.reference.label}>
        <LText variant="Body/B1/Medium" selectable>
          {order.reference.value}
        </LText>
      </Row>
    ) : null}
    {order.total ? (
      <Row label={MERCH_ORDER_COPY.total}>
        <LText variant="Body/B1/Medium">{order.total}</LText>
      </Row>
    ) : null}
  </LBox>
);

const Message = ({ text }: { text: string }) => (
  <LBox alignItems="center" justifyContent="center" flex={1}>
    <LText variant="h1">{text}</LText>
  </LBox>
);

/**
 * My order: a merch product the fan ordered, as the wallet opens it and the
 * order confirmation's app button lands on it. The product's picture and
 * name, then each order (newest first) with the address it is posted to,
 * and the same Contact as an event's page for anything else. The address
 * comes from the order's own read; until it has answered (or offline), the
 * order shows from the wallet's cached read without it.
 */
export const MerchOrder = ({
  accessId,
  dismiss,
}: {
  accessId: string;
  dismiss: () => void;
}) => {
  const screen = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const order = useMerchOrder(accessId);
  const cached = useAccess(accessId);
  const notFound =
    order.error instanceof TRPCClientError &&
    order.error.data?.code === "NOT_FOUND";
  const cachedAccess = cached.data as Access | undefined;
  const fromCache =
    cachedAccess?.type === "merch" && cachedAccess.orders?.length
      ? cachedAccess
      : null;
  const read = order.data?.access;
  const access: AccessTypeMerchType | null = notFound
    ? null
    : read?.type === "merch"
      ? read
      : fromCache;
  // Where a download is sent on to: the fan's Lissen email, read only for
  // a download (posted merch names no email).
  const email =
    useMe({ enabled: !!access?.orders?.some((each) => each.download) }).query
      .data?.email ?? null;
  const models = access
    ? (access.orders ?? []).map((each) => createMerchOrderModel(each, access.title, { email }))
    : [];

  return (
    <LBox flex={1}>
      <BackgroundCircles imageUrl={null} />
      <ScrollView
        contentContainerStyle={{
          backgroundColor: "transparent",
          padding: 20,
          paddingTop: theme.spacing.s4,
          paddingBottom:
            Platform.OS === "android"
              ? (initialWindowMetrics?.insets?.bottom ?? 20)
              : insets.bottom + theme.spacing.s3,
          gap: theme.spacing.m1,
          flexGrow: 1,
          width: getViewPort(screen).width,
        }}
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS !== "web" && <Handle />}
        <LBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {/* The close button's twin, unseen: it keeps the title centred. */}
          <LBox opacity={0} pointerEvents="none">
            <Close onPress={dismiss} sentryEventName="MerchOrder.Close" />
          </LBox>
          <LText variant="Heading/H2/SemiBold" textAlign="center">
            {MERCH_ORDER_COPY.title}
          </LText>
          <Close onPress={dismiss} sentryEventName="MerchOrder.Close" />
        </LBox>

        {!access ? (
          <Message
            text={
              notFound || (!order.isLoading && !cached.isLoading)
                ? MERCH_ORDER_COPY.notFound
                : MERCH_ORDER_COPY.loading
            }
          />
        ) : (
          <>
            <LBox flexDirection="row" gap="m1" alignItems="center">
              <LArtwork
                url={access.imageUrl ?? access.artistImage}
                size={85}
                borderRadius="m"
              />
              <LBox gap="s2" flex={1} minWidth={0}>
                <LText variant="Body/B1/Medium" color="whiteTranslucent60">
                  {access.artistName}
                </LText>
                <LText variant="Heading/H1/Bold">{access.title}</LText>
              </LBox>
            </LBox>
            {models.map((model) => (
              <OrderCard key={model.key} order={model} />
            ))}
            <LBox gap="s3" alignItems="flex-start">
              <LText variant="Body/B1/Regular" color="whiteTranslucent70">
                {MERCH_ORDER_COPY.help}
              </LText>
              <Contact
                width={THREE_BUTTON_LAYOUT_BUTTON_WIDTH}
                sentryEventName="MerchOrder.Contact"
              />
            </LBox>
          </>
        )}
      </ScrollView>
    </LBox>
  );
};

/** The route: the order of the access in its path, closed as a modal. */
const MerchOrderScreen = () => {
  const { accessId } = useLocalSearchParams<{ accessId: string }>();
  const dismiss = useCallback(() => router.back(), []);
  return <MerchOrder accessId={accessId} dismiss={dismiss} />;
};

export default MerchOrderScreen;
